import { ContextlessQueryObserverOptions, useSchemaInfiniteQuery } from '@frontend/react-query-helpers';
import { SchemaIO } from '@frontend/schema';
import { SchemaSyncConfigV1Service, serviceName } from '../service';
import { InfiniteQueryEndpointName, ServiceInfiniteQueries } from '../types';

const DEFAULT_OPTIONS: ContextlessQueryObserverOptions = {
  refetchOnWindowFocus: false,
};

export type UseSyncConfigV1InfiniteQueryArgs<
  EndpointName extends InfiniteQueryEndpointName,
  E = Error,
  D = SchemaIO<ServiceInfiniteQueries[EndpointName]>['output'],
  PageParam = undefined
> = Omit<
  Parameters<typeof useSchemaInfiniteQuery<ServiceInfiniteQueries, EndpointName, E, D, PageParam>>[0],
  'service' | 'serviceName'
>;
/**
 * A wrapper around useSchemaInfiniteQuery that uses the SyncConfigV1 service and serviceName.
 * This is intended to only be used internally in the `queries` directory of the library and not exported.
 */
export const useSyncConfigV1InfiniteQuery = <
  EndpointName extends InfiniteQueryEndpointName,
  E = Error,
  D = SchemaIO<ServiceInfiniteQueries[EndpointName]>['output'],
  PageParam = undefined
>({
  options,
  ...args
}: UseSyncConfigV1InfiniteQueryArgs<EndpointName, E, D, PageParam>) =>
  useSchemaInfiniteQuery<ServiceInfiniteQueries, EndpointName, E, D, PageParam>({
    service: SchemaSyncConfigV1Service,
    serviceName,
    ...args,
    options: {
      ...DEFAULT_OPTIONS,
      ...options,
    },
  });

export type UseSyncConfigV1InfiniteQueryEndpointArgs<
  EndpointName extends InfiniteQueryEndpointName,
  E = Error,
  D = SchemaIO<ServiceInfiniteQueries[EndpointName]>['output'],
  PageParam = undefined,
  Request = SchemaIO<ServiceInfiniteQueries[EndpointName]>['input']
> = Omit<UseSyncConfigV1InfiniteQueryArgs<EndpointName, E, D, PageParam>, 'endpointName' | 'request'> & {
  request: Request;
};
